// client/src/context/ToastContext.js
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

const STYLES = {
  success: { icon: CheckCircle2, bar: 'bg-primary-dark', iconColor: 'text-primary-dark' },
  error: { icon: XCircle, bar: 'bg-red-600', iconColor: 'text-red-600' },
  info: { icon: Info, bar: 'bg-accent-hard', iconColor: 'text-accent-hard' }
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((type, message, duration = 4000) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, type, message }]);
    if (duration) {
      setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const toast = {
    success: (message, duration) => push('success', message, duration),
    error: (message, duration) => push('error', message, duration),
    info: (message, duration) => push('info', message, duration)
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed z-[100] bottom-4 right-4 left-4 sm:left-auto flex flex-col gap-2 sm:w-96 pointer-events-none">
        {toasts.map((t) => {
          const style = STYLES[t.type] || STYLES.info;
          const Icon = style.icon;
          return (
            <div
              key={t.id}
              className="pointer-events-auto bg-white rounded-lg shadow-lg overflow-hidden flex items-stretch animate-[fadeIn_0.2s_ease-out]"
            >
              <div className={`w-1.5 ${style.bar}`} />
              <div className="flex items-start gap-2.5 p-3.5 flex-1 min-w-0">
                <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${style.iconColor}`} />
                <p className="text-sm text-dim flex-1 break-words">{t.message}</p>
                <button
                  onClick={() => dismiss(t.id)}
                  className="text-gray-400 hover:text-gray-600 shrink-0"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
};
